/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/require-default-props */
import {
  Divider,
  List,
  ListItem,
  ListItemProps,
  ListItemText,
} from '@material-ui/core';
import {
  DateType,
  DateTypeRange,
  transformToDateOrNull,
} from '@src/utils/fnsDateUtils';
import {
  endOfDay,
  isBefore,
  isDate,
  isSameDay,
  isValid,
  startOfDay,
} from 'date-fns';
import { createContext, useCallback, useContext, useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useIntl } from 'react-intl';
import ReactDatePickerStyled from './ReactDatePickerStyled';

export type CalendarProps<WithRange extends boolean | undefined = undefined> = {
  className?: string;
  monthsShown?: number;
  maxDate?: DateType;
  minDate?: DateType;
  leftPanel?: React.ReactNode;
  footer?: React.ReactNode;
  timeFormat?: string;
  disabled?: boolean;
  minTime?: Date;
  maxTime?: Date;
  filterTime?: (date: Date) => boolean;
} & (WithRange extends true
  ? {
      selectsRange: WithRange;
      value: DateTypeRange;
      onChange: (value: DateTypeRange) => void;
      showTimeSelect?: false;
      showTimeSelectOnly?: false;
    }
  : {
      selectsRange?: false;
      value: DateType;
      onChange: (value: DateType) => void;
      showTimeSelect?: boolean;
      showTimeSelectOnly?: boolean;
    });

const CalendarContext = createContext<{
  value: unknown;
  minDate: Date | null;
  maxDate: Date | null;
  disabled?: boolean;
  setValue(value: unknown): void;
}>({
  value: undefined,
  minDate: null,
  maxDate: null,
  setValue: () => {},
});

export const useCalendarContext = () => useContext(CalendarContext);

function Calendar<WithRange extends boolean | undefined = undefined>({
  className,
  selectsRange,
  value,
  onChange,
  leftPanel,
  footer,
  showTimeSelect,
  showTimeSelectOnly,
  minTime,
  maxDate,
  minDate,
  maxTime,
  filterTime,
  disabled,
  ...props
}: CalendarProps<WithRange>) {
  const { formatMessage } = useIntl();
  const changeHandler = useCallback(
    (nextValue: typeof value) => {
      if (disabled) return;
      if (selectsRange) {
        let [start, end] = nextValue as DateTypeRange;
        if (isValid(start)) start = startOfDay(start as Date);
        if (isValid(end)) end = endOfDay(end as Date);
        onChange([start, end]);
      } else {
        onChange(nextValue as DateType);
      }
    },
    [selectsRange, onChange, disabled]
  );

  if (selectsRange) {
    const [start, end] = value as DateTypeRange;
    return (
      <CalendarContext.Provider
        value={{
          value: [transformToDateOrNull(start), transformToDateOrNull(end)],
          minDate: transformToDateOrNull(minDate),
          maxDate: transformToDateOrNull(maxDate),
          disabled,
          setValue: changeHandler,
        }}
      >
        <ReactDatePickerStyled className="nowrap">
          {leftPanel && (
            <>
              <List dense className="calendar-left-panel">
                {leftPanel}
              </List>
              <Divider orientation="vertical" flexItem />
            </>
          )}
          <div className="datepicker">
            <ReactDatePicker
              className={className}
              inline
              selectsRange
              selected={start}
              onChange={changeHandler}
              startDate={start}
              endDate={end}
              maxDate={maxDate}
              minDate={minDate}
              disabled={disabled}
              {...props}
            />
            {footer && <div className="footer">{footer}</div>}
          </div>
        </ReactDatePickerStyled>
      </CalendarContext.Provider>
    );
  }
  return (
    <ReactDatePickerStyled className="nowrap">
      <ReactDatePicker
        className={className}
        inline
        selected={value}
        onChange={changeHandler}
        showTimeSelect={showTimeSelect || showTimeSelectOnly}
        showTimeSelectOnly={showTimeSelectOnly}
        timeCaption={formatMessage({ id: 'DatePicker.TimeCaption' })}
        minTime={minTime}
        maxTime={maxTime}
        maxDate={maxDate}
        minDate={minDate}
        filterTime={filterTime}
        disabled={disabled}
        {...props}
      />
    </ReactDatePickerStyled>
  );
}

export default Calendar;

export type CalendarItemOptionProps<T> = Omit<
  ListItemProps<any>,
  'onSelect' | 'value'
> & {
  label: React.ReactNode;
  value: T | (() => T);
  checked?: boolean;
  onChange?: (
    value: T extends any[] ? DateTypeRange : T,
    checked: boolean
  ) => void;
  onSelect?: (value: T extends any[] ? DateTypeRange : T) => void;
};

export const CalendarItemOption = <T,>({
  label,
  value,
  checked,
  onChange,
  onSelect,
  className,
  ...props
}: CalendarItemOptionProps<T>) => {
  const {
    value: calendarValue,
    minDate,
    disabled: calendarDisabled,
    setValue,
  } = useCalendarContext();

  const isRange = valueIsArray(calendarValue);

  const selected = useMemo(() => {
    if (checked !== undefined) return checked;

    if (isRange && valueIsArray(value)) {
      const [start, end] = getValue<DateTypeRange>(value);
      const [currentStart, currentEnd] = calendarValue;
      if (start === currentStart && end === currentEnd) return true;
      if (start && end && currentStart && currentEnd)
        return isSameDay(start, currentStart) && isSameDay(end, currentEnd);
      return false;
    }
    if (isDate(calendarValue)) {
      const date = getValue<Date>(value);
      return isSameDay(date as Date, calendarValue as Date);
    }

    return calendarValue === getValue(value);
  }, [calendarValue, checked, value, isRange]);

  const disabled = useMemo(() => {
    if (calendarDisabled) return true;

    if (isRange && valueIsArray(value)) {
      const [start] = getValue<DateTypeRange>(value);
      if (start && minDate) {
        return isBefore(start, minDate);
      }
    }
    return false;
  }, [calendarDisabled, isRange, minDate, value]);

  return (
    <ListItem
      className={`calendar-item-option ${className}`}
      {...props}
      selected={selected}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        const nextValue = getValue<T>(value);
        onChange?.(nextValue, !selected);
        if (onSelect) onSelect(nextValue);
        else if (valueIsDate(nextValue)) setValue(nextValue);
      }}
      button
    >
      <ListItemText>{label}</ListItemText>
    </ListItem>
  );
};

function getValue<T>(value: unknown): T extends any[] ? DateTypeRange : T {
  if (typeof value === 'function') return value();
  return value as any;
}

function valueIsArray(value: unknown): value is any[] {
  if (Array.isArray(value)) return true;
  if (typeof value === 'function') return valueIsArray(value());
  return false;
}

function valueIsDate(value: unknown): boolean {
  if (Array.isArray(value)) return isDate(value[0]) && isDate(value[1]);
  if (isDate(value)) return true;
  return false;
}
